import { Box, BoxProps, Flex, Heading, Spinner } from '@chakra-ui/react';
import axios from 'axios';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';

import Markdown from '@/components/Markdown';
import { RegistryPackage } from '@/graphql/upbound-graphql';
import { findDocsPageUrl } from '@/utils/helpers/docs';

import DocsTree from './DocsTree';

const TabContentDocs: React.FC<BoxProps & { data: RegistryPackage }> = ({ data, ...props }) => {
  const router = useRouter();

  const requestedPageName = useMemo(() => {
    const pageName = router.query.pageName;

    if (!pageName) {
      return '';
    }

    if (!Array.isArray(pageName)) {
      return pageName;
    }

    return pageName.join('/');
  }, [router]);

  const [pageMarkdown, setPageMarkdown] = useState('');

  const { selectedPage } = useMemo(
    () => ({
      selectedPage: findDocsPageUrl({
        tableOfContents: data.docs?.tableOfContents || [],
        requestedPageName,
      }),
    }),
    [data, requestedPageName],
  );

  useEffect(() => {
    if (!selectedPage || requestedPageName === selectedPage.name) {
      return;
    }

    async function getPageMarkdown() {
      const result = await axios.get<string | number>(selectedPage.location);

      setPageMarkdown(result.data.toString());
    }

    getPageMarkdown();
  }, [requestedPageName, selectedPage]);

  if (!data.docs) {
    return null;
  }

  return (
    <Flex width="100%" id="docs" {...props} flexDirection={{ base: 'column', lg: 'row' }}>
      <Box width="100%" maxWidth={{ base: 'auto', lg: '270px' }}>
        <Flex flexDirection="column" width="100%" position="sticky" top="0px">
          <Box width="100%" maxHeight="100vh" overflow="auto">
            <Heading
              as="h3"
              fontSize="14px"
              lineHeight="20px"
              mt="38px"
              pb="12px"
              pl="8px"
              fontWeight={600}
              color="grey.2"
            >
              Contents
            </Heading>
            <DocsTree
              tableOfContents={data.docs.tableOfContents}
              selectedPage={selectedPage}
              incompleteParams={{
                packageType: data.packageType,
                packageOwner: data.account,
                packageName: data.name,
                packageVersion: data.version,
              }}
            />
          </Box>
        </Flex>
      </Box>
      <Box
        minWidth={{ base: '100%', lg: '70%' }}
        pt="12px"
        borderLeftWidth={{ base: '0', lg: '1px' }}
        borderLeftColor="white.4"
        borderLeftStyle="solid"
        borderTopWidth={{ base: '1px', lg: '0' }}
        borderTopColor="white.4"
        borderTopStyle="solid"
        pl={{ base: '12px', lg: '80px' }}
        minHeight="500px"
      >
        {pageMarkdown ? (
          <Markdown lineHeight="25px" fontSize="14px" color="grey.0">
            {pageMarkdown}
          </Markdown>
        ) : (
          <Flex alignItems="center" justifyContent="center">
            <Spinner role="Spinner" color="black.0" />
          </Flex>
        )}
      </Box>
    </Flex>
  );
};
export default TabContentDocs;
